








































































































































































































import {
  computed, defineComponent, PropType, Ref, ref, watch,
} from 'vue';
import { WorkspacePermissionsSpec, UserSpec } from 'multinet';
import { cloneDeep, debounce } from 'lodash';
import api from '@/api';

import store from '@/store';
import {
  Role,
  SingularRole,
  RoleLevel,
} from '@/utils/permissions';

export interface UserPermissionSpec {
  role: Role;
  user: UserSpec;
}

export interface UserSearchResult extends UserSpec {
  listing: string;
}

const assignableRoleListing: SingularRole[] = ['maintainer', 'writer', 'reader'];
const totalRoleListing: SingularRole[] = [...assignableRoleListing, 'owner'];

export default defineComponent({
  name: 'PermissionsDialog',
  props: {
    workspace: {
      type: String as PropType<string>,
      required: true,
    },
  },
  setup(props) {
    const permDialog = ref(false);
    const mutablePermissions: Ref<WorkspacePermissionsSpec | null> = ref(null);
    const userSearchString: Ref<string | null> = ref(null);
    const userSearchResults: Ref<UserSearchResult[]> = ref([]);
    const newUserSelection: Ref<UserSpec[]> = ref([]);

    const publicToggle = ref(true);
    watch(publicToggle, (val) => {
      if (mutablePermissions.value) {
        mutablePermissions.value.public = val;
      }
    });

    const filteredWorkspacePermissions = computed(() => {
      // Used to remove the `public` field in the permissions object.
      if (mutablePermissions.value === null) { return null; }

      const {
        owner, maintainers, readers, writers,
      } = mutablePermissions.value;

      return {
        owner,
        maintainers,
        readers,
        writers,
      };
    });
    const userPermissionsList = computed(() => {
      const userPermissionList: UserPermissionSpec[] = [];
      if (filteredWorkspacePermissions.value === null) {
        return userPermissionList;
      }

      const roles = Object.keys(
        filteredWorkspacePermissions.value,
      ) as Role[];

      roles.forEach((role) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const roleUsers = filteredWorkspacePermissions.value![role];

        if (roleUsers) {
          if (role === 'owner') {
            userPermissionList.push({ role, user: roleUsers as UserSpec });
          } else {
            (roleUsers as UserSpec[]).forEach((user: UserSpec) => {
              userPermissionList.push({ role, user });
            });
          }
        }
      });

      return userPermissionList;
    });

    async function searchUsers(query: string) {
      if (userSearchString.value === null) { return; }

      const userPermissionList = userPermissionsList.value as UserPermissionSpec[];
      const userInWorkspace = (user: UserSpec) => (userPermissionList.find((userPerm) => userPerm.user.id === user.id));

      const result = await api.searchUsers(query);
      const mappedResults: UserSearchResult[] = result
        .map((user) => ({ ...user, listing: `${user.first_name} ${user.last_name} (${user.email})` }))
        .filter((user) => !userInWorkspace(user));

      userSearchResults.value = mappedResults;
    }
    const throttledUserSearch = debounce(searchUsers, 200);

    const workspacePermissionsEditable = computed(() => store.getters.permissionLevel >= RoleLevel.maintainer);

    function initMutableData(permissions: WorkspacePermissionsSpec) {
      mutablePermissions.value = cloneDeep(permissions);
      publicToggle.value = permissions.public;
    }

    // Workspace permissions setup
    const permissions: Ref<WorkspacePermissionsSpec | null> = ref(null);
    api.getWorkspacePermissions(props.workspace).then((data) => { permissions.value = data; });
    watch(permissions, (val) => {
      if (val !== null) {
        initMutableData(val);
      }
    });

    function singularRoleToPlural(role: SingularRole): Role {
      if (role === 'owner') { return role; }

      return `${role}s` as Role;
    }
    function pluralRoleToSingular(role: Role): SingularRole {
      if (role === 'owner') { return role; }

      return role.slice(0, -1) as SingularRole;
    }
    function setRoleForUser(user: UserSpec, currentRole: Role, newRole: Role) {
      if (mutablePermissions.value === null) {
        return;
      }

      // Currently don't allow changing owners
      if (newRole !== currentRole && newRole !== 'owner' && currentRole !== 'owner') {
        mutablePermissions.value[newRole].push(user);
        mutablePermissions.value[currentRole] = mutablePermissions.value[currentRole].filter((x) => x.id !== user.id);
      }
    }
    function setRoleForAllUsers(role: Role) {
      if (mutablePermissions.value === null) {
        return;
      }
      const userList: UserSpec[] = userPermissionsList.value
        .filter((user: UserPermissionSpec) => user.role !== 'owner')
        .map((user) => user.user);

      mutablePermissions.value = {
        owner: mutablePermissions.value.owner,
        public: mutablePermissions.value.public,
        maintainers: [],
        writers: [],
        readers: [],
        [role]: userList,
      };
    }
    function removeUserPermissions(user: UserSpec, currentRole: Role) {
      if (currentRole === 'owner' || mutablePermissions.value === null) return;

      const newRoleList = mutablePermissions.value[currentRole].filter((x) => x.id !== user.id);

      mutablePermissions.value[currentRole] = newRoleList;
    }
    async function setPermissions() {
      if (mutablePermissions.value === null) {
        return;
      }
      try {
        await api.setWorkspacePermissions(props.workspace, mutablePermissions.value);
        permDialog.value = false;
      } catch (error) {
        // TODO #205
      }
    }
    function resetUserSearch() {
      newUserSelection.value = [];
      userSearchResults.value = [];
      userSearchString.value = null;
    }
    function cancelChange() {
      if (permissions.value === null) {
        return;
      }
      permDialog.value = false;
      initMutableData(permissions.value);
      resetUserSearch();
    }
    function addSelectedUsers() {
      if (mutablePermissions.value === null) {
        return;
      }

      mutablePermissions.value.readers.push(...newUserSelection.value);
      resetUserSearch();
    }

    return {
      assignableRoleListing,
      totalRoleListing,
      permDialog,
      publicToggle,
      mutablePermissions,
      throttledUserSearch,
      userSearchString,
      userSearchResults,
      newUserSelection,
      workspacePermissionsEditable,
      setPermissions,
      cancelChange,
      userPermissionsList,
      pluralRoleToSingular,
      addSelectedUsers,
      singularRoleToPlural,
      setRoleForUser,
      setRoleForAllUsers,
      removeUserPermissions,
    };
  },
});
